import * as pinyin from 'tiny-pinyin';
import { Base64 } from '../file';

export class PinyinTerminator {
    private colors: Array<string> = [
        '#F09581',
        '#D981B1',
        '#48B4E9',
        '#99C1EB',
        '#4863E9',
        '#93989E',
        '#66BDAB',
        '#A6C8AB',
        '#A37AD0',
        '#D49E6A',
        '#A6CBC7',
    ];

    /**
     * 汉字转拼音
     * @param {string} char
     * @returns {string}
     */
    convertToPinyin = (char: string) => {
        return (pinyin.isSupported() ? pinyin.convertToPinyin(char) : char).charAt(0);
    };

    /**
     * 获取随机颜色
     */
    getRandomColor = () => this.colors[Math.floor(Math.random() * this.colors.length - 1)];

    /**
     * 获取名字简称（姓名首字母组合）
     * @param names
     */
    getAbbreviation = (names: string[]): string =>
        names
            .map((name: string) => {
                return name ? this.convertToPinyin(name.charAt(0).toUpperCase()) : 'X';
            })
            .join(' ');

    /**
     * 文字转图片（base64编码）
     * @param names
     * @param type
     * @param color
     * @param bgColor
     * @param font
     * @param textAlign
     * @param textBaseline
     */
    wordsToImage = (
        names: string[],
        type: 'base64' | 'blob' = 'blob',
        { color, bgColor, font, textAlign, textBaseline }: any = {},
    ): any => {
        const canvas = document.createElement('canvas');

        canvas.width = 200;
        canvas.height = 200;

        const ctx = canvas.getContext('2d');
        const abbr = this.getAbbreviation(names);

        // 画圆
        ctx.fillStyle = bgColor || this.getRandomColor();
        ctx.fillRect(0, 0, 200, 200);
        ctx.stroke();

        ctx.scale(2, 2);
        // 描绘文字
        ctx.fillStyle = color || '#ffffff';
        ctx.font = font || '64px Hind_Vadodar, Verdana';
        ctx.textAlign = textAlign || 'center';
        ctx.textBaseline = textBaseline || 'middle';
        ctx.fillText(abbr, 50, 50);

        return type === 'blob'
            ? Base64.convertToBlob(canvas.toDataURL('image/png'), 'image/png')
            : canvas.toDataURL('image/png');
    };
}

class Contact {
    /**
     * 手机和邮箱号加*处理
     * @param type
     * @param data
     */
    formatContact = (type?: 'phone' | 'email', data: string = ''): any => {
        if (data.includes('****')) return data;
        switch (type) {
            case 'phone':
                return `****${data.slice(-4)}`;
            case 'email': {
                if (String(data).indexOf('@') > 0) {
                    const str = data.split('@');
                    return `${str[0].substr(0, 3)}****@${str[1]}`;
                }
                break;
            }
            default:
                return data;
        }
    };

    /**
     * 填充链接
     * @param url
     * @param extra
     */
    fillUrl = (url: string, extra: string = 'http') => {
        let link = (url || '').replace(/\s+/g, '');
        link = !link.includes('://') ? `://${url || ''}` : url;
        const [protocol, uri] = link.split('://');
        return `${['https', 'http'].includes(protocol) ? protocol : extra}://${uri}`;
    };

    /**
     * 资料库链接转相对路径
     * @param url
     */
    relativeUrl = (url: string = '') => {
        let src = url;
        // Detect the gateway-storage URL shape via plain string scan rather than
        // a regex — Sonar's backtracking heuristic flags `[^/]+/.../[^/]+/storage`
        // even though it's linear, and the original `/http(.+)gateway(.+)storage/`
        // was a real ReDoS hazard (two adjacent greedy `.+` quantifiers).
        // Plain `indexOf` is O(n·k) with k = 2 fixed, no backtracking at all.
        // / 用 indexOf 字符串扫描替代正则来识别网关存储 URL 形状 —— Sonar 的回溯启发式
        // 仍然把 `[^/]+/.../[^/]+/storage` 误判为 ReDoS，而原写法
        // `/http(.+)gateway(.+)storage/` 是真正的 ReDoS 风险（两个贪婪 .+ 相邻）。
        // 纯 indexOf 是 O(n·2)，完全没有回溯。
        if (
            src &&
            (src.startsWith('https://') || src.startsWith('http://')) &&
            (() => {
                const gatewayIdx = src.indexOf('/gateway/');
                if (gatewayIdx < 0) return false;
                const storageIdx = src.indexOf('/storage', gatewayIdx + '/gateway/'.length);
                return storageIdx > gatewayIdx;
            })()
        ) {
            src = new URL(src).pathname;
        }
        // 开发环境使用dev网关
        // Ensure exactly ONE slash between devHost and src, regardless of whether
        // devHost ends with `/` (crm-sales hardcodes `https://.../`) or src starts
        // with `/` (pathname extraction always yields a leading `/`). Without
        // this guard the result is `https://...tech//gateway/...` (double slash).
        // / 保证 devHost 与 src 之间恰好一个 /。devHost 可能末尾带 /（crm-sales 硬编码），
        // pathname 抽出来必然开头带 /，不规范化就会拼成 `https://...tech//gateway/...`。
        if (src && !src.includes('http') && window.devHost) {
            // Trim trailing `/` from devHost and leading `/` from src using plain
            // string ops instead of /\/+$/ and /^\/+/ — Sonar's backtracking
            // heuristic flags any regex with a `+` quantifier, even when the
            // pattern is anchored and can't actually backtrack. The `while` form
            // is linear and removes as many slashes as are present (not just one).
            // / 用 endsWith/startsWith + slice 替代 /\/+$/ 和 /^\/+/ —— Sonar 的回溯
            // 启发式对任何带 `+` 量词的 regex 都会报警，哪怕这个 regex 是锚定的、
            // 根本不会回溯。while 循环是线性的，会剥掉所有连续的斜杠（不止一个）。
            let devHost = window.devHost;
            while (devHost.endsWith('/')) devHost = devHost.slice(0, -1);
            while (src.startsWith('/')) src = src.slice(1);
            src = `${devHost}/${src}`;
        }
        return src;
    };

    /**
     * 文字转图片
     * @param names
     * @param type
     * @param from
     * @param to
     * @param color
     * @param bgColor
     */
    wordsToImage = (
        names: string[] | string,
        type: 'base64' | 'blob' = 'blob',
        { from, to, color, bgColor }: any = {
            from: 0,
            to: 2,
        },
    ): string | Blob => {
        const str = Array.isArray(names) ? names : (names || 'X').substr(from, to).split('');
        return new PinyinTerminator().wordsToImage(str, type, {
            color: color,
            bgColor: bgColor,
        });
    };

    /**
     * 过滤非数字字符
     */
    number = (value: string) => value.replace(/[^0-9]/gi, '');

    /**
     * 地区
     * @param location
     * @param values
     * @param char
     */
    formatLocation = (location: any[], values: any[], char?: string) => {
        let ret: any[] = [];
        const getStaticView = (items: any[], deepIndex: number) => {
            let index = deepIndex;
            for (let { value, label, children } of items) {
                if (value === values[index]) {
                    ret.push(label);
                    Array.isArray(children) && getStaticView(children, ++index);
                    return;
                }
            }
        };
        getStaticView(location, 0);
        return ret.join(char || '');
    };
}

export default new Contact();
